import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, API_BASE } from '../constants';
import { fmtDateTime, mlColor, dirStyle, computePnL, detailHref, sizeCategory } from '../helpers';
import { buildTriggerIndex, resolveTrigger } from '../utils/algoResolver';
import GradeBadge from './badges/GradeBadge';
import ActionBadge from './badges/ActionBadge';
import SystemBadge from './badges/SystemBadge';
import CategoryBadge from './badges/CategoryBadge';
import TierBadge from './badges/TierBadge';
import SlotLeaders from './SlotLeaders';

// Column header order (must match the <td> render order in the body).
const COLS = ['Time', 'System', 'Algos', 'Asset', 'Timeframe', 'Trading Hour',
              'Dir', 'Action', 'Grade', 'Tier', 'Cat', 'ML', 'Size', 'P/L', 'Gate / Reason'];

// Columns that sort numerically; everything else sorts as text.
const NUMERIC_COLS = new Set(['Time', 'ML', 'Size', 'P/L', 'Trading Hour']);

// Normalise a cell value to a stable string; blanks collapse to "—" so a user
// can explicitly filter the blank category.
const norm = (v) => (v === null || v === undefined || v === '') ? '—' : String(v);

function escapeCsv(val) {
  if (val == null) return '';
  const s = String(val);
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCSV(decisions, label) {
  const headers = [
    'Time', 'Strategy', 'Algos', 'Asset', 'Timeframe', 'Trading Hour',
    'Direction', 'Action', 'Grade', 'Tier', 'Category', 'Size Cat', 'ML Score',
    'Contracts', 'Entry Price', 'Stop Price', 'Target Price',
    'Exit Price', 'PL Points', 'PL USD', 'Exit Reason',
    'Range Name', 'Cell', 'Phase', 'Gate / Reason',
  ];

  const csvTriggerIndex = buildTriggerIndex(decisions);
  const rows = decisions.map(d => {
    const pnl = computePnL(d);
    const exitPx = d.trade?.exit_px ?? d.exit_px ?? d.exit_price ?? '';
    const { trigger, isLuxConfluencing } = resolveTrigger(d, csvTriggerIndex);
    const algosCell = trigger
      ? (isLuxConfluencing ? `${trigger}+LUX` : trigger)
      : '';
    return [
      d.signal_ts ? new Date(d.signal_ts).toLocaleString('sv-SE', { timeZone: 'America/New_York' }) + ' ET' : '',
      d.system_id || '',
      algosCell,
      d.asset || d.ticker || '',
      d.timeframe || d.bar_timeframe || '',
      d.trading_hour ?? '',
      d.direction || '',
      (d.action_label || d.action) || '',
      d.grade || '',
      d.tier_num || d.tier || '',
      d.category || '',
      (() => { const sc = sizeCategory(d); return sc ? sc.catLabel : ''; })(),
      Math.round(parseFloat(d.ml_score) || 0),
      (d.action_label || d.action) === 'TRADE' ? (d.contracts ?? d.size_c ?? '') : '',
      d.entry_price ?? d.entry_px ?? '',
      d.stop_price ?? '',
      d.target_price ?? '',
      exitPx,
      pnl ? pnl.pts : '',
      pnl?.usd ?? d.pl_usd ?? d.pl_dollars ?? '',
      d.exit_reason || '',
      d.range_name || '',
      d.cell || '',
      d.phase || '',
      d.gate_blocked || d.reason || '',
    ].map(escapeCsv).join(',');
  });

  const csv = [headers.join(','), ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ts = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `imole_trades_${label}_${ts}.csv`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export default function HistoryTable({ decisions, showSystem, selected, onSelect, feedLabel }) {
  // Build a same-bar primary-algo index once per decisions update so every
  // row can resolve its trigger (LUX-confluence aware) at render time.
  const triggerIndex = useMemo(() => buildTriggerIndex(decisions), [decisions]);
  const navigate = useNavigate();

  // ── Per-column filtering ────────────────────────────────────────────────
  // Each filterable column maps a header label to an accessor returning the
  // row's value for that column. A non-empty Set in `filters[header]` keeps
  // only rows whose value is in the Set; empty/absent = show all.
  const accessors = useMemo(() => ({
    'System':       d => d.system_id,
    'Algos':        d => resolveTrigger(d, triggerIndex).trigger,
    'Asset':        d => d.asset || d.ticker,
    'Timeframe':    d => d.timeframe || d.bar_timeframe,
    'Trading Hour': d => d.trading_hour,
    'Dir':          d => d.direction,
    'Action':       d => d.action_label || d.action,
    'Grade':        d => d.grade,
    'Tier':         d => d.tier_num || d.tier,
    // Cat shows the Concentrate size category (CAT 1/2/3); fall back to the
    // legacy strategy-type for non-Concentrate rows. Keep this in step with the
    // <td> render + CSV so the filter options match the badges.
    'Cat':          d => { const sc = sizeCategory(d); return sc ? sc.catLabel : d.category; },
  }), [triggerIndex]);

  const [filters, setFilters] = useState({});      // { header: Set<string> }
  const [openCol, setOpenCol] = useState(null);     // header whose dropdown is open
  const [anchor, setAnchor] = useState({ top: 0, left: 0 });
  const dropRef = useRef(null);
  const [sort, setSort] = useState({ col: null, dir: null });  // Excel-style column sort

  // Distinct options per column, from the FULL set (so the list stays stable
  // regardless of what's currently filtered).
  const options = useMemo(() => {
    const m = {};
    Object.keys(accessors).forEach(h => {
      const set = new Set();
      decisions.forEach(d => set.add(norm(accessors[h](d))));
      m[h] = Array.from(set).sort();
    });
    return m;
  }, [decisions, accessors]);

  const activeCols = Object.entries(filters).filter(([, s]) => s && s.size > 0);

  const filtered = useMemo(() => {
    if (activeCols.length === 0) return decisions;
    return decisions.filter(d =>
      activeCols.every(([h, s]) => s.has(norm(accessors[h](d)))));
  }, [decisions, filters, accessors]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Excel-style sort: value accessor per column (numeric vs text) ────────
  const sortValue = (h, d) => {
    switch (h) {
      case 'Time':          return d.signal_ts ? new Date(d.signal_ts).getTime() : 0;
      case 'ML':            return parseFloat(d.ml_score) || 0;
      case 'Size':          return parseFloat(d.contracts ?? d.size_c) || 0;
      case 'P/L':           { const p = computePnL(d); return p ? p.pts : -Infinity; }
      case 'Trading Hour':  return parseFloat(d.trading_hour) || 0;
      case 'Gate / Reason': return d.gate_blocked || d.reason || '';
      default:              return accessors[h] ? norm(accessors[h](d)) : '';
    }
  };

  const sorted = useMemo(() => {
    if (!sort.col || !sort.dir) return filtered;
    const h = sort.col, numeric = NUMERIC_COLS.has(h);
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = sortValue(h, a), vb = sortValue(h, b);
      const cmp = numeric
        ? (va - vb)
        : String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVal = (h, opt) => {
    setFilters(prev => {
      const next = { ...prev };
      const set = new Set(next[h] || []);
      set.has(opt) ? set.delete(opt) : set.add(opt);
      if (set.size === 0) delete next[h]; else next[h] = set;
      return next;
    });
  };
  const clearCol = (h) => setFilters(prev => { const n = { ...prev }; delete n[h]; return n; });
  const clearAll = () => setFilters({});

  // Click a header to cycle its sort: asc → desc → off.
  const cycleSort = (h) => setSort(prev =>
    prev.col !== h ? { col: h, dir: 'asc' }
    : prev.dir === 'asc' ? { col: h, dir: 'desc' }
    : { col: null, dir: null });
  // Explicit Excel-style menu choice (from the column dropdown); closes the menu.
  const applySort = (h, dir) => { setSort({ col: h, dir }); setOpenCol(null); };

  const openFilterFor = (h, e) => {
    e.stopPropagation();
    if (openCol === h) { setOpenCol(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setAnchor({ top: r.bottom + 4, left: r.left });
    setOpenCol(h);
  };

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!openCol) return;
    const onDown = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpenCol(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpenCol(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openCol]);

  /**
   * Click behaviour:
   *   - plain click        → select the row (updates FeaturedDecision) AND navigate
   *                          to the detail page.
   *   - ⌘/Ctrl/middle-click → let the browser open /decisions/:system/:key in a
   *                          new tab (we do NOT call preventDefault, and we
   *                          return early so we don't also call navigate here)
   */
  const handleRowClick = (d, e) => {
    onSelect?.(d);

    const href = detailHref(d);
    if (!href) return;

    // Honour modifier / middle-click for "open in new tab"
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      window.open(href, '_blank', 'noopener');
      return;
    }

    navigate(href);
  };

  return (
    <div className="hist-wrap">
      <div className="hist-header">
        <span className="hist-title">Decision Log</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            title="Desk sequencing is live: each book defers to whoever holds its asset×session slot (one position per slot)."
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#2dd4bf', background: 'rgba(45,212,191,0.12)',
              border: '1px solid rgba(45,212,191,0.38)', borderRadius: 20,
              padding: '3px 9px', whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2dd4bf', boxShadow: '0 0 6px #2dd4bf' }} />
            Sequencing activated
          </span>
          <span className="hist-count">
            {filtered.length}{activeCols.length > 0 ? ` / ${decisions.length}` : ''}
          </span>
        </div>
      </div>
      <div style={{ padding: '6px 14px', borderBottom: `1px solid ${T.border}`,
                    display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {activeCols.length > 0 && (
          <button
            onClick={clearAll}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 600,
              background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 4,
              color: T.text, cursor: 'pointer', pointerEvents: 'all',
            }}
            title="Remove all column filters"
          >
            ✕ Clear filters ({activeCols.length})
          </button>
        )}
        {filtered.length > 0 && (
          <button
            onClick={() => downloadCSV(filtered, feedLabel || 'all')}
            style={{
              padding: '5px 14px', fontSize: 11, fontWeight: 600,
              background: T.blue, border: 'none', borderRadius: 4,
              color: '#ffffff', cursor: 'pointer', pointerEvents: 'all',
            }}
            title="Export the rows currently shown (filters applied)"
          >
            ↓ Download CSV (this view)
          </button>
        )}
        {/* Per-strategy full history — shown only on a single-strategy tab */}
        {feedLabel && feedLabel !== 'unified' && (
          <button
            onClick={() => window.open(
              `${API_BASE}/strategies/api/strategies/trade-export/?strategy=${encodeURIComponent(feedLabel)}`,
              '_blank')}
            style={{
              padding: '5px 14px', fontSize: 11, fontWeight: 600,
              background: '#0d9488', border: 'none', borderRadius: 4,
              color: '#ffffff', cursor: 'pointer', pointerEvents: 'all',
            }}
            title="Download THIS strategy's complete history — start to finish, server-side"
          >
            ↓ Download Full History ({feedLabel.toUpperCase()})
          </button>
        )}
        <button
          onClick={() => window.open(
            `${API_BASE}/strategies/api/strategies/trade-export/`, '_blank')}
          style={{
            padding: '5px 14px', fontSize: 11, fontWeight: 600,
            background: '#16a34a', border: 'none', borderRadius: 4,
            color: '#ffffff', cursor: 'pointer', pointerEvents: 'all',
          }}
          title="Download the COMPLETE history — every decision across every strategy, server-side"
        >
          ↓ Download Full History (all strategies)
        </button>
      </div>
      {decisions.length === 0 ? (
        <div className="empty-state">
          <span style={{ fontSize: 12 }}>No decisions yet</span>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <table className="hist-table">
            <thead>
              <tr>
                {COLS.map(h => {
                  const filterable = !!accessors[h];
                  const active = filters[h] && filters[h].size > 0;
                  return (
                    <th key={h} style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                      <span
                        onClick={() => cycleSort(h)}
                        title={`Sort by ${h}`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {h}
                        {sort.col === h && (
                          <span style={{ marginLeft: 3, color: T.blue }}>
                            {sort.dir === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </span>
                      {filterable && (
                        <button
                          onClick={(e) => openFilterFor(h, e)}
                          title={`Filter by ${h}`}
                          style={{
                            marginLeft: 5, padding: '0 2px', cursor: 'pointer',
                            background: 'none', border: 'none', fontSize: 9,
                            color: active ? T.blue : T.textMuted, verticalAlign: 'middle',
                          }}
                        >
                          ▼{active ? ` ${filters[h].size}` : ''}
                        </button>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((d, i) => {
                const isSelected = selected &&
                  selected.signal_ts === d.signal_ts &&
                  selected.system_id === d.system_id;
                const hasHref = !!detailHref(d);
                // TIER strategies expose two fields: `action` ("entry" / "exit",
                // the row kind) and `action_label` ("TRADE" / "BLOCK" / "EXIT" /
                // "OBSERVE", the verdict). Other strategies put the verdict in
                // `action` directly. Prefer `action_label` when present so a
                // BLOCKed tier row doesn't render as a soft "entry" chip.
                const actionEff = d.action_label || d.action;
                return (
                <tr
                  key={`${d.system_id}-${d.signal_ts}-${i}`}
                  onClick={(e) => handleRowClick(d, e)}
                  onAuxClick={(e) => { if (e.button === 1) handleRowClick(d, e); }}
                  title={hasHref ? 'Click for full decision detail · Ctrl/⌘-click to open in new tab' : undefined}
                  style={{
                    cursor: hasHref ? 'pointer' : 'default',
                    background: isSelected ? 'rgba(41,98,255,0.08)' : (d._cascadeViolation ? 'rgba(224,138,138,0.09)' : undefined),
                    outline: isSelected ? '1px solid rgba(41,98,255,0.25)' : (d._cascadeViolation ? '1px solid rgba(224,138,138,0.28)' : undefined),
                  }}
                >
                  <td className="mono" style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{fmtDateTime(d.signal_ts)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <SystemBadge systemId={d.system_id} />
                      {d._cascadeViolation && (
                        <span title={`Cascade violation — ${d._cascadeViolation}`} style={{
                          marginLeft: 5, color: '#e08a8a', fontSize: 9, fontWeight: 700,
                          border: '1px solid #e08a8a55', borderRadius: 3, padding: '0 4px' }}>
                          ⚠ cascade
                        </span>
                      )}
                    </div>
                    {d._slotLeaders && d._slotLeaders.length > 0 && (
                      <div style={{ marginTop: 3 }}><SlotLeaders leaders={d._slotLeaders} compact /></div>
                    )}
                  </td>
                  {(() => {
                    const { trigger, isLuxConfluencing } = resolveTrigger(d, triggerIndex);
                    return (
                      <td className="mono" style={{
                        color: trigger ? T.text : T.textMuted, fontSize: 11, whiteSpace: 'nowrap',
                      }}
                          title={isLuxConfluencing
                            ? `${trigger} (confirmed by LUX) · signal_id: ${d.signal_id}`
                            : (d.signal_id || 'No signal_id')}>
                        {trigger || '—'}
                        {isLuxConfluencing && (
                          <span style={{ color: T.textMuted, marginLeft: 4, fontSize: 9 }}>+ LUX</span>
                        )}
                      </td>
                    );
                  })()}
                  <td className="mono" style={{ color: T.text, fontSize: 11 }}>{d.asset || d.ticker || '—'}</td>
                  <td className="mono" style={{ color: T.textDim, fontSize: 11 }}>{d.timeframe || d.bar_timeframe || '—'}</td>
                  <td className="mono" style={{ color: T.textDim, fontSize: 11 }}>{d.trading_hour ?? '—'}</td>
                  <td>
                    {d.direction
                      ? <span className="badge" style={{ ...dirStyle(d.direction), fontSize: 9, padding: '1px 6px' }}>{d.direction}</span>
                      : <span style={{ color: T.textMuted }}>—</span>}
                  </td>
                  <td><ActionBadge action={actionEff} /></td>
                  <td><GradeBadge grade={d.grade} decision={d} /></td>
                  <td><TierBadge tier={d.tier_num || d.tier} /></td>
                  <td>{(() => {
                    // Concentrate size weight (1.5/1.0/0.5×) for the books in the
                    // source-discipline kit; legacy strategy-type for the rest.
                    const sc = sizeCategory(d);
                    return sc
                      ? <span title={sc.tip} className="badge" style={{
                          background: `${sc.color}18`, border: `1px solid ${sc.color}40`,
                          color: sc.color, fontSize: 9, letterSpacing: '0.06em' }}>
                          {sc.catLabel}
                        </span>
                      : <CategoryBadge category={d.category} />;
                  })()}</td>
                  <td className="mono" style={{ fontWeight: 700, color: mlColor(d.ml_score) }}>
                    {Math.round(parseFloat(d.ml_score) || 0)}
                  </td>
                  <td className="mono" style={{ color: actionEff === 'TRADE' ? T.bull : T.textMuted }}>
                    {(() => {
                      // contracts for most systems; FAVOLA carries size_c. Guard
                      // against undefined so we never render "undefinedc".
                      const sz = d.contracts ?? d.size_c;
                      return actionEff === 'TRADE' && sz != null ? `${sz}c` : '—';
                    })()}
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const p = computePnL(d);
                      if (!p) return <span style={{ color: T.textMuted }}>—</span>;
                      const color = p.pts > 0 ? T.bull : p.pts < 0 ? T.bear : T.textDim;
                      const sign  = p.pts > 0 ? '+' : '';
                      return (
                        <span style={{ color, fontWeight: 700 }}>
                          {sign}{p.pts}pt{p.usd != null ? ` (${p.usd > 0 ? '+' : ''}$${p.usd})` : ''}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ color: T.textMuted, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.gate_blocked || (d.reason?.slice(0, 40)) || '—'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Column-filter dropdown (fixed-position popover so the table's overflow
          can't clip it). One open at a time. */}
      {openCol && (
        <div
          ref={dropRef}
          style={{
            position: 'fixed', top: anchor.top, left: anchor.left, zIndex: 1000,
            background: T.surface || '#161b22', border: `1px solid ${T.border}`,
            borderRadius: 6, padding: 8, minWidth: 170, maxHeight: 320, overflowY: 'auto',
            boxShadow: '0 6px 22px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{openCol}</span>
            <button
              onClick={() => clearCol(openCol)}
              style={{ fontSize: 10, background: 'none', border: 'none', color: T.blue, cursor: 'pointer' }}
            >
              Clear
            </button>
          </div>

          {/* Sort options — Excel-style labeled menu */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2,
                        marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${T.border}` }}>
            {[['asc',  NUMERIC_COLS.has(openCol) ? '↑  Sort 1 → 9' : '↑  Sort A → Z'],
              ['desc', NUMERIC_COLS.has(openCol) ? '↓  Sort 9 → 1' : '↓  Sort Z → A']].map(([dir, label]) => {
              const on = sort.col === openCol && sort.dir === dir;
              return (
                <button
                  key={dir}
                  onClick={() => applySort(openCol, dir)}
                  style={{
                    textAlign: 'left', fontSize: 11, padding: '4px 6px', borderRadius: 4,
                    cursor: 'pointer', border: 'none',
                    background: on ? `${T.blue}22` : 'transparent',
                    color: on ? T.blue : T.text, fontWeight: on ? 700 : 400,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted,
                        letterSpacing: '0.08em', marginBottom: 4 }}>
            FILTER
          </div>
          {options[openCol]?.length === 0 && (
            <div style={{ fontSize: 11, color: T.textMuted }}>No values</div>
          )}
          {options[openCol]?.map(opt => {
            const checked = filters[openCol]?.has(opt) ?? false;
            return (
              <label key={opt}
                     style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 11,
                              padding: '3px 2px', cursor: 'pointer', color: T.text }}>
                <input type="checkbox" checked={checked} onChange={() => toggleVal(openCol, opt)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
